if (!customElements.get('bundle-selector')) {
  customElements.define('bundle-selector', class extends HTMLElement {
    connectedCallback() {
      this.parents = [...this.querySelectorAll('[data-parent]')];
      this.offers = [...this.querySelectorAll('[data-offer]')];
      this.visuals = [...this.querySelectorAll('[data-visual]')];
      this.offerGroups = [...this.querySelectorAll('[data-offer-group]')];
      this.visualRequestId = 0;
      this.status = this.querySelector('[data-status]');
      const sectionId = this.closest('.shopify-section')?.id || '';
      const templatePrefix = sectionId.includes('__')
        ? sectionId.slice(0, sectionId.lastIndexOf('__') + 2)
        : 'shopify-section-';
      this.builder = this.dataset.builderSection
        ? document.getElementById(`${templatePrefix}${this.dataset.builderSection}`)
          || document.getElementById(`shopify-section-${this.dataset.builderSection}`)
        : null;
      this.builder?.setAttribute('data-bundle-selector-builder', '');
      this.setBuilderFormat(this.parents[0]);
      this.builderWasHidden = this.builder?.hidden || false;
      this.requestId = 0;
      this.abortController = new AbortController();
      const options = { signal: this.abortController.signal };
      this.addEventListener('click', (event) => {
        const parent = event.target.closest('[data-parent]');
        const offer = event.target.closest('[data-offer]');
        if (parent && this.parents.includes(parent)) this.selectParent(parent);
        if (offer && this.offers.includes(offer) && !offer.disabled) this.switchOffer(offer);
      }, options);
      this.addEventListener('shopify:block:select', (event) => {
        const parent = this.parents.find((item) => item.dataset.parent === event.detail.blockId);
        const offer = this.offers.find((item) => item.dataset.blockId === event.detail.blockId);
        if (parent) this.selectParent(parent);
        if (offer) {
          const owner = this.parents.find((item) => item.dataset.groupKey === offer.dataset.groupKey);
          if (owner) this.selectParent(owner, offer);
        }
      }, options);
      if (this.builder) {
        this.priceObserver = new MutationObserver(() => this.updateBundlePrices());
        this.priceObserver.observe(this.builder, { childList: true, subtree: true, characterData: true });
        this.updateBundlePrices();
      }
      if (this.parents[0]) this.selectParent(this.parents[0]);
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.priceObserver?.disconnect();
      this.visualRequestId += 1;
      this.requestId += 1;
      if (this.builder) {
        this.builder.hidden = this.builderWasHidden;
        this.builder.removeAttribute('data-bundle-selector-builder');
        this.builder.removeAttribute('data-bundle-format');
        this.builder.removeAttribute('data-bundle-summary-label');
        this.builder.style.removeProperty('--bundle-summary-label');
      }
    }

    updateBundlePrices() {
      // EB's SDK prefixes the euro even when the store sets symbolOnLeft to false.
      const amounts = this.builder.querySelectorAll(
        '.gbbProductPrice, .gbbProductCompareAtPrice, .gbbFooterDiscountedTotalValue, .gbbFooterOriginalTotalValue, ' +
        '.gbbFooterTotalValue, .gbbFooterProductPrice, .gbbFooterProductCompareAtPrice, .gbbFooterBreakdownAmount, ' +
        '.gbbVariantDrawerOptionPrice, .gbbVariantDrawerProductPrice, .gbbCartToggleTotal'
      );
      amounts.forEach((amount) => {
        for (const node of amount.childNodes) {
          if (node.nodeType !== Node.TEXT_NODE) continue;
          const match = /^€(-?\d+(?:[.,]\d+)?)$/.exec(node.data.trim());
          if (match) node.data = match[1].replace('.', ',') + '\u00a0€';
        }
      });

      this.builder.querySelectorAll('.gbbFullPageBundle .gbbFooterActionContainer').forEach((footer) => {
        if (footer.closest('.gbbProductsFooterHTMLForMobile')) return;
        const button = footer.querySelector('.gbbFooterNextButton');
        if (!button) return;
        const total = footer.querySelector('.gbbFooterDiscountedTotalValue')?.textContent.trim();
        let price = button.querySelector('.bundle-selector__button-total');
        if (!total) {
          price?.remove();
          return;
        }
        if (!price) {
          price = document.createElement('span');
          price.className = 'bundle-selector__button-total';
          button.appendChild(price);
        }
        const text = ` • ${total}`;
        if (price.textContent !== text) price.textContent = text;
      });
    }

    setBuilderFormat(parent) {
      if (!this.builder || !parent) return;
      this.builder.dataset.bundleFormat = parent.dataset.groupKey;
      const label = parent.dataset.summaryLabel?.trim();
      if (label) {
        this.builder.setAttribute('data-bundle-summary-label', '');
        this.builder.style.setProperty('--bundle-summary-label', JSON.stringify(label));
      } else {
        this.builder.removeAttribute('data-bundle-summary-label');
        this.builder.style.removeProperty('--bundle-summary-label');
      }
    }

    selectParent(parent, preferredOffer) {
      this.requestId += 1;
      this.removeAttribute('aria-busy');
      this.status.textContent = '';
      this.parents.forEach((item) => item.setAttribute('aria-pressed', String(item === parent)));
      const showFormat = () => {
        this.offerGroups.forEach((group) => {
          const active = group.dataset.offerGroup === parent.dataset.parent;
          group.dataset.active = String(active);
          group.inert = !active;
          if (active) group.removeAttribute('aria-hidden');
          else group.setAttribute('aria-hidden', 'true');
        });
      };
      const offer = preferredOffer && !preferredOffer.disabled
        ? preferredOffer
        : this.offers.find((item) => parent.dataset.groupKey && item.dataset.groupKey === parent.dataset.groupKey && !item.disabled);
      if (offer) {
        this.switchOffer(offer, showFormat);
      } else {
        showFormat();
        this.selectVisual(null);
        this.offers.forEach((item) => item.setAttribute('aria-pressed', 'false'));
        if (this.builder) this.builder.hidden = true;
      }
    }

    async selectVisual(offerBlockId) {
      const requestId = ++this.visualRequestId;
      const next = this.visuals.find((item) => item.dataset.visual === offerBlockId);
      const current = this.visuals.find((item) => !item.hidden);
      if (!next) {
        this.visuals.forEach((item) => { item.hidden = true; });
        return;
      }
      if (next === current) return;
      const currentPicture = current?.querySelector('picture');
      const nextPicture = next.querySelector('picture');
      // Shared artwork stays in place instead of swapping identical lazy images.
      if (currentPicture && nextPicture && currentPicture.innerHTML === nextPicture.innerHTML) return;
      const image = next.querySelector('img');
      if (image && typeof image.decode === 'function') {
        image.loading = 'eager';
        try {
          await image.decode();
        } catch {
          // Keep the existing visual if the replacement could not be loaded.
          if (!image.naturalWidth) return;
        }
      }
      if (requestId !== this.visualRequestId || !this.isConnected) return;
      this.visuals.forEach((item) => { item.hidden = item !== next; });
    }

    preserveSlotLayout(slot) {
      if (!slot?.isConnected) return () => {};
      // The SDK strips the outgoing slot's classes before waiting for init.
      // Preserve its box so that brief wait cannot expand the product grid.
      const computed = getComputedStyle(slot);
      const properties = ['width', 'max-width', 'padding', 'margin', 'box-sizing'];
      const styles = properties.map((property) => ({
        property,
        value: slot.style.getPropertyValue(property),
        priority: slot.style.getPropertyPriority(property),
        computed: computed.getPropertyValue(property),
      }));
      styles.forEach(({ property, computed: value }) => slot.style.setProperty(property, value));
      return () => styles.forEach(({ property, value, priority }) => {
        if (value) slot.style.setProperty(property, value, priority);
        else slot.style.removeProperty(property);
      });
    }

    async prepareProductImages(slot) {
      const images = [...(slot?.querySelectorAll('.gbbProductImage') || [])];
      if (!images.length) return;
      let timer;
      try {
        // The incoming SDK slot is display:none; lazy images must load before reveal.
        await Promise.race([
          Promise.all(images.map((image) => {
            image.loading = 'eager';
            return typeof image.decode === 'function' ? image.decode().catch(() => {}) : Promise.resolve();
          })),
          new Promise((resolve) => { timer = setTimeout(resolve, 1500); }),
        ]);
      } finally {
        clearTimeout(timer);
      }
    }

    async switchSdkOffer(sdk, offerId) {
      const runtime = window.gbbSideFooter;
      const template = runtime?.template;
      if (sdk.bundles?.[offerId]?.templateName !== 'FBP_SIDE_FOOTER'
        || typeof template?.init !== 'function') {
        return sdk.switchTo(offerId);
      }

      // Easy Bundle's full-page destroy() clears window.gbb in setTimeout(0).
      // Its immediate init() otherwise sees that old instance and skips rendering.
      // Yield only at the init boundary, after the SDK has called destroy().
      const outgoingSlot = sdk.bundles?.[sdk.current]?._slotEl;
      const restoreLayout = this.preserveSlotLayout(outgoingSlot);
      const selector = this;
      const originalInit = template.init;
      const deferredInit = async function(...args) {
        // Otherwise the new mobile footer clones the outgoing drawer without its toggle method.
        outgoingSlot?.querySelector('.gbbCartDrawer')?.destroy?.();
        if (window.gbb === runtime) {
          await new Promise((resolve) => setTimeout(resolve, 0));
        }
        const result = await originalInit.apply(this, args);
        // Keep the outgoing grid visible until the replacement photographs are decoded.
        await selector.prepareProductImages(sdk.bundles?.[offerId]?._slotEl);
        return result;
      };
      template.init = deferredInit;
      try {
        return await sdk.switchTo(offerId);
      } finally {
        if (template.init === deferredInit) template.init = originalInit;
        restoreLayout();
      }
    }

    async switchOffer(offer, showFormat) {
      const requestId = ++this.requestId;
      this.visualRequestId += 1;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = '';
      // Let Easy Bundle retain the current content until the replacement is ready.
      // Hiding the section here collapses the page during every switch.
      if (this.builder) this.builder.hidden = this.builderWasHidden;
      try {
        // The application loads asynchronously, after the theme section.
        const deadline = Date.now() + 10000;
        while (typeof window.gbbBundlePageSdk?.switchTo !== 'function') {
          if (requestId !== this.requestId || !this.isConnected) return;
          if (Date.now() >= deadline) throw new Error('Easy Bundle SDK unavailable');
          await new Promise((resolve) => setTimeout(resolve, 200));
        }
        if (requestId !== this.requestId || !this.isConnected) return;
        // Serialize SDK calls so a slow earlier switch cannot overwrite the latest choice.
        const previousSwitch = this.pendingSwitch || Promise.resolve();
        const pendingSwitch = previousSwitch.catch(() => {}).then(async () => {
          if (requestId !== this.requestId || !this.isConnected) return;
          const result = await this.switchSdkOffer(window.gbbBundlePageSdk, offer.dataset.offer);
          if (result === false) throw new Error('Easy Bundle rejected the offer');
        });
        this.pendingSwitch = pendingSwitch;
        await pendingSwitch;
        if (requestId !== this.requestId || !this.isConnected) return;
        this.setBuilderFormat(this.parents.find((parent) => parent.dataset.groupKey === offer.dataset.groupKey));
        showFormat?.();
        this.selectVisual(offer.dataset.blockId);
        this.offers.forEach((item) => item.setAttribute('aria-pressed', String(item === offer)));
        if (this.builder) this.builder.hidden = this.builderWasHidden;
      } catch (error) {
        if (requestId !== this.requestId || !this.isConnected) return;
        this.status.textContent = this.dataset.errorMessage;
        if (this.builder) this.builder.hidden = true;
        console.error('[Bundle selector]', error);
      } finally {
        if (requestId === this.requestId) this.removeAttribute('aria-busy');
      }
    }
  });
}
